'use client';

import React, { forwardRef } from 'react';
import {
  ArrowRight,
  Check,
  Copy,
  KeyRound,
  Loader2 as Spinner,
  Plus,
  RefreshCcw,
  Save,
  Send,
  Upload,
  X,
} from 'lucide-react';

/**
 * Variante = cor/peso do botao, SEMPRE do tema (nunca estilizar por fora).
 * PADRAO GLOBAL DE QUANDO USAR CADA (identidade visual):
 * - `primary`   -> acao principal da tela: criar/salvar/confirmar o fluxo (1 por contexto).
 * - `secondary` -> acao de apoio (ex.: Cancelar, acao secundaria do header).
 * - `ghost`     -> acao discreta/terciaria (toolbar, icon-only de baixo peso).
 * - `danger`    -> destrutiva solida (excluir definitivo).
 * - `outline-danger` -> destrutiva de menor enfase (ex.: Excluir no rodape CRUD).
 * - `success`   -> confirmacao positiva pontual (raro; CRUD usa `primary` p/ Salvar).
 * - `warning`/`info`/`neutral`/`soft` -> estados/realces neutros, sem hierarquia de CTA.
 * - `outline-primary`/`outline-success` -> mesma semantica, menor enfase (contorno).
 * - `link`      -> acao que parece link (texto, sem caixa).
 * - `admin`     -> contexto super-admin (tom administrativo).
 * Regra: a MESMA acao usa a MESMA variante em toda rota. Rodape de CRUD nao usa
 * Button solto - usa `CrudActions` (Salvar=primary / Cancelar=secondary / Excluir=outline-danger).
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'ghost'
  | 'success'
  | 'warning'
  | 'info'
  | 'neutral'
  | 'soft'
  | 'outline-primary'
  | 'outline-success'
  | 'outline-warning'
  | 'outline-danger'
  | 'link'
  | 'admin';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Alias de `leftIcon` (nome canonico do catalogo). */
  icon?: React.ReactNode;
  /** Alias de `rightIcon` (nome canonico do catalogo). */
  iconRight?: React.ReactNode;
  iconOnly?: boolean;
}

function toDsVariant(variant: ButtonVariant) {
  if (variant === 'secondary' || variant === 'ghost') return 'ghost';
  if (variant === 'link') return 'ghost';
  if (
    variant === 'admin' ||
    variant === 'success' ||
    variant === 'warning' ||
    variant === 'danger' ||
    variant === 'info' ||
    variant === 'neutral' ||
    variant === 'soft' ||
    variant === 'outline-primary' ||
    variant === 'outline-success' ||
    variant === 'outline-warning' ||
    variant === 'outline-danger'
  ) {
    return variant;
  }
  return 'primary';
}

function getButtonText(children: React.ReactNode) {
  return React.Children.toArray(children)
    .filter((child): child is string | number => typeof child === 'string' || typeof child === 'number')
    .join(' ')
    .trim()
    .toLowerCase();
}

function getSemanticIcon(children: React.ReactNode) {
  const text = getButtonText(children);
  if (!text) return null;
  if (text.includes('cancelar') || text.includes('fechar')) return <X aria-hidden="true" />;
  if (text.includes('salvar')) return <Save aria-hidden="true" />;
  if (text.includes('alterar senha')) return <KeyRound aria-hidden="true" />;
  if (text.includes('adicionar') || text.includes('novo') || text.includes('nova')) return <Plus aria-hidden="true" />;
  if (text.includes('confirmar') || text.includes('cadastrar') || text.includes('concluir')) return <Check aria-hidden="true" />;
  if (text.includes('copiar')) return <Copy aria-hidden="true" />;
  if (text.includes('enviar') || text.includes('disparar')) return <Send aria-hidden="true" />;
  if (text.includes('atualizar') || text.includes('reprocessar') || text.includes('sincronizar')) return <RefreshCcw aria-hidden="true" />;
  if (text.includes('upload') || text.includes('trocar logo')) return <Upload aria-hidden="true" />;
  if (text.includes('ver ') || text.includes('abrir') || text.includes('continuar')) return <ArrowRight aria-hidden="true" />;
  return null;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      icon,
      iconRight,
      iconOnly = false,
      children,
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const resolvedLeftIcon = leftIcon ?? icon;
    const resolvedRightIcon = rightIcon ?? iconRight;
    // Icone semantico automatico so quando NENHUM icone explicito foi passado -
    // com rightIcon presente ele duplicava a seta ("→ Continuar →").
    const semanticIcon =
      !iconOnly && !resolvedLeftIcon && !resolvedRightIcon && !loading
        ? getSemanticIcon(children)
        : null;

    return (
      <button
        ref={ref}
        type={type}
        className={`btn btn--${toDsVariant(variant)} btn--${size}${iconOnly ? ' btn--icon-only' : ''}${fullWidth ? ' btn--block' : ''}${className ? ` ${className}` : ''}`}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Spinner size={16} aria-hidden="true" /> : resolvedLeftIcon ?? semanticIcon}
        {!iconOnly && children && <span>{children}</span>}
        {iconOnly && children}
        {!loading && resolvedRightIcon}
      </button>
    );
  },
);

Button.displayName = 'Button';
